import React from "react";
import type { SortingState } from "@tanstack/react-table";
import type { ScheduleTableRow } from "../../../models";
import Spinner from "../../../components/Spinner/Spinner";
import DebouncedSearchInput from "../../../components/DebouncedSearchInput/DebouncedSearchInput";
import VirtualScheduleTable from "./VirtualScheduleTable/VirtualScheduleTable";
import "./ScheduleView.css";

interface Props {
  rows: ScheduleTableRow[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  search: string;
  onSearchChange: (value: string) => void;
  searchResetKey: number | null;
  excludeCompleted: boolean;
  onExcludeCompletedChange: (value: boolean) => void;
  criticalOnly: boolean;
  onCriticalOnlyChange: (value: boolean) => void;
  /** Identity of the current query scope. The virtual table remounts when this
   *  changes so scroll resets to the top and the row window can't render past
   *  the end of a newly selected (and possibly shorter) section. */
  scopeKey: string;
}

const ScheduleView: React.FC<Props> = ({
  rows,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  sorting,
  onSortingChange,
  search,
  onSearchChange,
  searchResetKey,
  excludeCompleted,
  onExcludeCompletedChange,
  criticalOnly,
  onCriticalOnlyChange,
  scopeKey,
}) => {
  return (
    <div className="schedule-view">
      <div className="table-controls">
        <DebouncedSearchInput
          key={searchResetKey ?? "none"}
          onChange={onSearchChange}
          placeholder="Search activities..."
          aria-label="Search activities"
        />
        <label className="schedule-toggle">
          <input
            type="checkbox"
            checked={excludeCompleted}
            onChange={(e) => onExcludeCompletedChange(e.target.checked)}
          />
          Hide completed
        </label>
        <label className="schedule-toggle">
          <input
            type="checkbox"
            checked={criticalOnly}
            onChange={(e) => onCriticalOnlyChange(e.target.checked)}
          />
          Critical path only
        </label>
      </div>

      <Spinner visible={loading} label="Loading schedule..." />

      <VirtualScheduleTable
        key={scopeKey}
        rows={rows}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
        sorting={sorting}
        onSortingChange={onSortingChange}
        search={search}
      />
    </div>
  );
};

export default ScheduleView;
